/**
 * 狂一鍋 interval 訂位流程的 API 封裝。
 *
 * 端點與 payload 形狀全部沿用 prototype/booking.html 已驗證跑通的版本，
 * 只把 fetch 換成 Windwalker 的 u.$http（baseURL 在 entry 設為 API_ENDPOINT）。
 *
 * 與明水然（slot 模式）共用同一組後端端點；差別只在
 * 可訂時段走 intervalAvailability、送單時 meals 恆為空陣列（三火鍋品牌不選餐點）。
 */
import { apiLang } from '@vue/services/interval-lang';
/**
 * 從多層 data 包裝中挖出含指定鍵的物件。
 *
 * 後端框架會自動替回傳值包一層 data，各端點包裝層數不一致，
 * 寫死 res.data.data 會在某些端點讀到 undefined。
 */
export function unwrap(json, keys) {
    let d = json;
    for (let i = 0; i < 5 && d && typeof d === 'object'; i++) {
        if (keys.some((k) => k in d)) {
            return d;
        }
        d = d.data;
    }
    return {};
}
/** 逐層挖出布林值（checkCustomerDuplicateBooking 直接回 bool）。 */
function unwrapBool(json) {
    let d = json;
    for (let i = 0; i < 5 && d && typeof d === 'object'; i++) {
        if (typeof d.data === 'boolean') {
            return d.data;
        }
        d = d.data;
    }
    return false;
}
/**
 * 載入本品牌可訂門市。
 *
 * 過濾條件三個都必要：品牌、interval 佔用模式、上架中。
 * 少了 occupancyMode 會把明水然系（slot）的店也撈進來，那些店查
 * intervalAvailability 會查不到規則、整排顯示無時段。
 */
export async function loadStores(brandId) {
    const res = await u.$http.get('front/reserve/loadStores');
    const items = res.data?.data?.items ?? [];
    return items.filter((s) => s.brandId === brandId && s.occupancyMode === 'interval' && s.status === 1);
}
/** 查單店當日、指定人數的可訂時段。 */
export async function loadAvailability(storeId, date, party) {
    const res = await u.$http.get('front/reserve/intervalAvailability', {
        params: { store_id: storeId, date, party },
    });
    return res.data?.data?.items ?? [];
}
/**
 * 查訂位資格（是否需要 OTP、是否被封鎖）。
 *
 * name 照送：contactChanged（聯絡資料變了就重驗）已於 api 1.7.3.0 停用，
 * 後端只是收下不再用它判定。留著是為了「事前查詢與最終送出的欄位一致」，
 * 日後要恢復那條規則時不必再繞一次前端。
 */
export async function checkEligibility(countryCode, phone, email, date, name) {
    const res = await u.$http.get('front/reserve/checkBookingEligibility', {
        params: { country_code: countryCode, phone, date, email, name },
    });
    return unwrap(res.data, ['requiresVerification', 'blocked', 'verificationType']);
}
/**
 * 查是否已有重複訂位（同品牌每位客戶同時僅一筆未來有效訂位）。
 *
 * 查詢失敗回 false 不阻斷流程：後端 reserving 仍會把關，
 * 不因這道提前提示掛掉就讓客人訂不了位。
 */
export async function checkDuplicateBooking(countryCode, phone, storeId, date) {
    const res = await u.$http.get('front/reserve/checkCustomerDuplicateBooking', {
        params: { country_code: countryCode, phone, store_id: storeId, date },
    });
    return unwrapBool(res.data);
}
/** 送出 OTP（type='email' 走 Email、其餘走簡訊）。 */
export async function sendOtp(type, countryCode, phone, email, date, recaptchaToken, name, verificationId = '') {
    const url = type === 'email' ? 'front/reserve/sendEmailOtp' : 'front/reserve/sendOtp';
    const res = await u.$http.post(url, {
        data: {
            email,
            countryCode,
            phone,
            date,
            // 重發時一定要送：後端拿它辨識「這是同一次訂位流程的重寄」，
            // 少了它，碼一過期或驗證過就會掉回資格判定，+886 客會被
            // 「此資料不使用 Email 驗證」擋死在驗證頁（2026-09-09 正式回報的死路）。
            verificationId,
            // name 照送：contactChanged（改名就重驗）已於 api 1.7.3.0 停用，
            // 後端只是收下不再用它判定；留著是為了與最終送出的欄位一致。
            name,
            gRecaptchaResponse: recaptchaToken,
            language: apiLang(),
        },
    });
    // 只用 'ok' 當鍵：外層 envelope 有 top-level 'code'（HTTP 狀態）但無 'ok'，避免挖錯層。
    return unwrap(res.data, ['ok', 'verificationId']);
}
/** 驗證 OTP，成功時回傳 otpVerifiedToken 供建單消費。 */
export async function verifyOtp(type, verificationId, countryCode, phone, email, otp) {
    const isEmail = type === 'email';
    const url = isEmail ? 'front/reserve/verifyEmailOtp' : 'front/reserve/verifyOtp';
    const payload = isEmail
        ? { verificationId, email, otp }
        : { verificationId, countryCode, phone, otp };
    const res = await u.$http.post(url, { data: payload });
    return unwrap(res.data, ['ok', 'otpVerifiedToken']);
}
/**
 * 建單（與明水然共用 reserving；interval 分支會配桌、寫佔用、產短網址、寄簡訊/Email）。
 *
 * meals 恆為空陣列：狂一鍋現場點餐不選餐點，後端
 * prepareOrderTotalAndOrderItems 本就容忍空餐點。
 */
export async function reserving(payload) {
    const res = await u.$http.post('front/reserve/reserving', {
        data: {
            ...payload,
            meals: [],
            language: apiLang(),
        },
    });
    return res.data;
}
